import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  Avatar,
  Divider,
  Chip,
  FormControlLabel,
  Switch,
  Alert,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Send as SendIcon,
  Note as NoteIcon,
  Lock as LockIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import publicApplicationsService from '../services/publicApplicationsService';

const NotesTimeline = ({ applicationId, notes, onNoteAdded }) => {
  const [noteText, setNoteText] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!noteText.trim()) {
      setError('Note text is required');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await publicApplicationsService.addNote(applicationId, noteText, isPrivate);

      // Reset form
      setNoteText('');
      setIsPrivate(false);

      if (onNoteAdded) {
        onNoteAdded();
      }
    } catch (error) {
      console.error('Error adding note:', error);
      setError(error.response?.data?.message || 'Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <Box>
      {/* Add Note Form */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: '#f5f5f5' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#29204C' }}>
          Add New Note
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Type your note here..."
            variant="outlined"
            sx={{ mb: 2 }}
          />

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  {isPrivate ? <LockIcon fontSize="small" /> : <PublicIcon fontSize="small" />}
                  <Typography variant="body2">
                    {isPrivate ? 'Private Note' : 'Public Note'}
                  </Typography>
                </Stack>
              }
            />

            <Button
              type="submit"
              variant="contained"
              startIcon={<SendIcon />}
              disabled={submitting || !noteText.trim()}
              sx={{
                bgcolor: '#29204C',
                '&:hover': { bgcolor: '#1a1430' },
              }}
            >
              {submitting ? 'Adding...' : 'Add Note'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Notes Timeline */}
      <Box>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#29204C' }}>
          Notes History
        </Typography>

        {notes && notes.length > 0 ? (
          <Timeline position="right">
            {notes.map((note, index) => (
              <TimelineItem key={note.id || index}>
                <TimelineOppositeContent
                  sx={{ flex: 0.3, py: 3 }}
                  color="text.secondary"
                  variant="body2"
                >
                  {formatDate(note.created_at)}
                </TimelineOppositeContent>

                <TimelineSeparator>
                  <TimelineDot
                    sx={{
                      bgcolor: note.is_private ? '#9c27b0' : '#29204C',
                      p: 1,
                    }}
                  >
                    {note.is_private ? (
                      <LockIcon fontSize="small" sx={{ color: 'white' }} />
                    ) : (
                      <NoteIcon fontSize="small" sx={{ color: 'white' }} />
                    )}
                  </TimelineDot>
                  {index < notes.length - 1 && <TimelineConnector />}
                </TimelineSeparator>

                <TimelineContent sx={{ py: 3 }}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      bgcolor: note.is_private ? '#f3e5f5' : 'white',
                      border: note.is_private ? '1px solid #ce93d8' : 'none',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: '#29204C',
                            fontSize: '0.875rem',
                          }}
                        >
                          {getInitials(note.user?.name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {note.user?.name || 'Unknown User'}
                          </Typography>
                          {note.user?.email && (
                            <Typography variant="caption" color="text.secondary">
                              {note.user.email}
                            </Typography>
                          )}
                        </Box>
                      </Stack>

                      {note.is_private && (
                        <Chip
                          icon={<LockIcon fontSize="small" />}
                          label="Private"
                          size="small"
                          color="secondary"
                          sx={{ height: 24 }}
                        />
                      )}
                    </Stack>

                    <Divider sx={{ mb: 1.5 }} />

                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {note.note_text}
                    </Typography>
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f5f5' }}>
            <NoteIcon sx={{ fontSize: 64, color: '#bdbdbd', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No notes yet. Add the first note above.
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default NotesTimeline;
